var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import SearchAPI from '../../services/SearchAPI';
import { MODULE_SURFACE, PAGE_TYPE, REQUEST_PURPOSE, trackingQuery } from '../../framework/constants/tracking';
import UserActionsAPI from '../../services/UserActionsAPI';
import _get from 'lodash/get';
import { observable, action, makeObservable } from 'mobx';
import { transformAggregation, transformDataForVehicles } from '../../framework/DataTransformers';
import { CARS_LIMIT } from '../../framework/constants/cars';
import { HTTP_TO_MANY_REQUESTS } from '../../framework/constants/app';
import { SEARCH_SORT_OPTIONS, SUPER_ADMIN_SEARCH_SORT_OPTIONS } from '../../framework/constants/data';
import { ALTERNATIVE_FILTER_KEYS, MIN_ALTERNATIVE_CARS } from '../../framework/constants/search';
import { Filters } from '../../framework/factories/FilterFactory';
import { filterRemover, resolveSortQuery } from '../../framework/utils/FiltersUtils';
import { ShowNotificationError } from '../../framework/ErrorLogger';
import AuthStore from '../AuthStore';
import AlternativeSearchAPI from '../../services/AlternativeSearchAPI';
import CarsAPI from '../../services/CarsAPI';
import { set } from 'lodash';
class SearchResultsStore {
    constructor() {
        Object.defineProperty(this, "results", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "alternatives", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "aggregation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "resultsCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "scrollPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "activePageNumber", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "isFetching", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "isReferenceSearch", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "successTransition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isErrorAlternativeField", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "activeSorting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "statsData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "filters", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: Filters
        });
        Object.defineProperty(this, "getSearchResult", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (filters, offset, sortingQuery, carIdFromAlternative, getStats, page) => {
                try {
                    const isAlternative = AuthStore.getAlternative();
                    const API = isAlternative ? AlternativeSearchAPI : SearchAPI;
                    // ADV-259 bugfix (found live on dev, 2026-09-17): this is the ACTUAL primary SRL data
                    // fetch (SearchNextPage/SearchDetailedNextPage) — the method originally tagged here
                    // (searchAlternatives, below) is a secondary reference-search helper, not this one. The
                    // 5th arg is AlternativeSearchAPI-specific (a real carId, used to build its /alternative
                    // URL) — SearchAPI.search's 5th param never read it before ADV-259 (silently dropped,
                    // pre-4-param signature), so repurposing it as trackingQuery for the non-alternative case
                    // changes nothing for AlternativeSearchAPI callers and fixes real SRL tagging for everyone else.
                    const fifthArg = isAlternative
                        ? carIdFromAlternative
                        : trackingQuery(PAGE_TYPE.SEARCH_RESULT_LIST, MODULE_SURFACE.SRL, REQUEST_PURPOSE.VISIBLE_VEHICLE_IMPRESSIONS);
                    const response = await API.search(filters, offset, CARS_LIMIT, sortingQuery, fifthArg);
                    // .then((response: any) => {
                    for (let i = 0; i < response.data.length; i++) {
                        this.results.set(offset + i, transformDataForVehicles(response.data[i]));
                    }
                    this.aggregation = transformAggregation(response.aggregation);
                    this.resultsCount = response.metadata.total.value;
                    this.isFetching = false;
                    if (getStats)
                        this.getStatsData(page);
                }
                catch (error) {
                    console.log('Search API error = ', error);
                    this.isFetching = false;
                    // 429 (gateway/Cloudflare rate limit): keep the previous results/aggregation on screen
                    // and show a toast. Never navigate — the old top-level redirect to the raw API URL
                    // stranded the user on a blank JSON/HTML error page (a rate-limit page is not a
                    // solvable challenge; the 503/cf-challenge case keeps its own redirect in BaseHttp).
                    if (error && typeof error === 'object' && error.status === HTTP_TO_MANY_REQUESTS) {
                        ShowNotificationError('errors.tooManyRequests');
                    }
                }
            }
        });
        Object.defineProperty(this, "search", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (filters, page = 1, getStats, carIdFromAlternative) => {
                const offset = CARS_LIMIT * page - CARS_LIMIT;
                const sortingObject = { ...SEARCH_SORT_OPTIONS, ...SUPER_ADMIN_SEARCH_SORT_OPTIONS };
                const sortingQuery = resolveSortQuery(sortingObject[this.activeSorting] || '', filters);
                if (this.results.has(offset) && this.activePageNumber === page)
                    return; //TODO: check reference page is correct, do not delete this string, it's needed to prevent an additional request when click the browser back from CDP
                this.isReferenceSearch = false;
                this.activePageNumber = page; // Needs for store activePageNumber for Reference search page in order to get rid of extra request when pagination page
                if (carIdFromAlternative)
                    this.isReferenceSearch = !!carIdFromAlternative;
                this.isFetching = true;
                this.getSearchResult(filters, offset, sortingQuery, carIdFromAlternative, getStats, page);
            }
        });
        Object.defineProperty(this, "getVehicleId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId) => {
                this.successTransition = false;
                this.isErrorAlternativeField = false;
                return CarsAPI.getVehicleId(carId)
                    .then((carId) => {
                    this.successTransition = true;
                    return _get(carId, '_id');
                })
                    .catch((_error) => {
                    this.isErrorAlternativeField = true;
                });
            }
        });
        Object.defineProperty(this, "searchAlternatives", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (filters) => {
                const sortOptions = { ...SEARCH_SORT_OPTIONS, ...SUPER_ADMIN_SEARCH_SORT_OPTIONS };
                const sortQuery = resolveSortQuery(sortOptions[this.activeSorting] || '', filters);
                const alternatives = new Map();
                const remove = filterRemover(filters, [...ALTERNATIVE_FILTER_KEYS]);
                const search = (offset) => {
                    return SearchAPI.search(filters, offset, CARS_LIMIT, sortQuery, trackingQuery(PAGE_TYPE.SEARCH_RESULT_LIST, MODULE_SURFACE.SRL, REQUEST_PURPOSE.VISIBLE_VEHICLE_IMPRESSIONS)).then(({ data, metadata: { total } }) => {
                        for (let i = 0; i < data.length; i++) {
                            const alternative = transformDataForVehicles(data[i]);
                            if (!alternatives.has(alternative.id)) {
                                alternatives.set(alternative.id, alternative);
                            }
                        }
                        if (alternatives.size < MIN_ALTERNATIVE_CARS) {
                            const nextOffset = offset + CARS_LIMIT;
                            if (nextOffset < total) {
                                return search(nextOffset);
                            }
                            if (remove()) {
                                return search(0);
                            }
                        }
                    });
                };
                return new Promise((resolve, reject) => {
                    this.isFetching = true;
                    if (remove()) {
                        search(0)
                            .then(() => {
                            this.alternatives = [...alternatives.values()].slice(0, MIN_ALTERNATIVE_CARS);
                        })
                            .then(resolve)
                            .catch(reject)
                            .finally(() => {
                            this.isFetching = false;
                        });
                    }
                    else {
                        resolve(undefined);
                    }
                });
            }
        });
        Object.defineProperty(this, "getCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (filters, isAlreadyTransformed) => {
                this.isFetching = true;
                return SearchAPI.getCount(filters, isAlreadyTransformed)
                    .then((response) => {
                    this.resultsCount = response.count;
                    return response.count;
                })
                    .catch((error) => {
                    console.log(error);
                })
                    .finally(() => {
                    this.isFetching = false;
                });
            }
        });
        Object.defineProperty(this, "clearSearchState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (clearSorting = true) => {
                this.results.clear();
                if (clearSorting)
                    this.activeSorting = '';
            }
        });
        Object.defineProperty(this, "clearSearchResults", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.clearSearchState();
                this.search(this.filters);
            }
        });
        Object.defineProperty(this, "getLeasingActive", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (firstRegistration, maxAge, mileage, maxMileage) => {
                const registration = firstRegistration ? firstRegistration * 1000 : Date.now();
                const maxCarAge = new Date().setMonth(new Date().getMonth() - maxAge);
                return registration > maxCarAge && maxMileage > mileage;
            }
        });
        Object.defineProperty(this, "getCarsForPage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (page) => {
                const offset = CARS_LIMIT * page - CARS_LIMIT;
                const results = this.results;
                const cars = [];
                for (let i = offset; i < (offset + CARS_LIMIT); i += 1) {
                    if (results.has(i)) {
                        const car = results.get(i);
                        if (car.price.leasing) {
                            set(car, 'price.leasing.isActive', this.getLeasingActive(car.info.regDate, car.price.leasing.maxAge, car.info.mileage, car.price.leasing.maxMileage));
                        }
                        cars.push(car);
                    }
                }
                return cars;
            }
        });
        Object.defineProperty(this, "getStatsDataByIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carIDs) => {
                return UserActionsAPI.getStatsData(carIDs)
                    .then((response) => {
                    response.data.forEach((objStats) => {
                        this.statsData.set(objStats.carId, objStats);
                    });
                })
                    .catch((_error) => {
                    ShowNotificationError('errors.loadStatistic');
                });
            }
        });
        Object.defineProperty(this, "getStatsData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (page) => {
                const offset = CARS_LIMIT * page - CARS_LIMIT;
                const results = this.results;
                const carIDs = [];
                for (let i = offset; i < (offset + CARS_LIMIT); i += 1) {
                    if (results.has(i))
                        carIDs.push(results.get(i).id);
                }
                this.getStatsDataByIds(carIDs);
            }
        });
        Object.defineProperty(this, "setActiveSorting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (newActiveSorting) => {
                this.activeSorting = newActiveSorting;
            }
        });
        Object.defineProperty(this, "setScrollPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (position) => {
                this.scrollPosition = position;
            }
        });
        makeObservable(this);
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "results", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "alternatives", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "aggregation", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "resultsCount", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "scrollPosition", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "activePageNumber", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "isFetching", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "isReferenceSearch", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "successTransition", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "isErrorAlternativeField", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "activeSorting", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "statsData", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "search", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "getVehicleId", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "searchAlternatives", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "getCount", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "clearSearchState", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "clearSearchResults", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "getCarsForPage", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "getStatsDataByIds", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "getStatsData", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "setActiveSorting", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], SearchResultsStore.prototype, "setScrollPosition", void 0);
export default new SearchResultsStore();
